import React, { useState, useEffect } from 'react';
import Layout from '../../Layout/Layout';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { 
  FaPlus, FaSearch, FaFilter, FaSort, 
  FaEdit, FaTrash, FaExclamationTriangle,
  FaSpinner, FaArrowUp, FaArrowDown,
  FaHome, FaCar, FaUtensils, FaShoppingCart, 
  FaPlane, FaGraduationCap, FaGift, FaMoneyBillWave,
  FaPiggyBank, FaChartLine, FaWallet, FaCreditCard
} from 'react-icons/fa';
import './TransactionCategories.css';

// API Configuration
const API_BASE_URL = 'http://localhost:8000/api';

// SweetAlert2 Configuration
const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true
});

const SavingsGoals = () => {
  const navigate = useNavigate();
  const [savings, setSavings] = useState([]);
  const [filteredSavings, setFilteredSavings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [showFilters, setShowFilters] = useState(false);

  // Icon mapping
  const iconMap = {
    FaHome: FaHome,
    FaCar: FaCar,
    FaUtensils: FaUtensils,
    FaShoppingCart: FaShoppingCart,
    FaPlane: FaPlane,
    FaGraduationCap: FaGraduationCap,
    FaGift: FaGift,
    FaMoneyBillWave: FaMoneyBillWave,
    FaPiggyBank: FaPiggyBank,
    FaChartLine: FaChartLine,
    FaWallet: FaWallet,
    FaCreditCard: FaCreditCard
  };

  // Load categories on component mount
  useEffect(() => {
    fetchSavings();
  }, []);

  // Filter and sort categories when dependencies change
  useEffect(() => {
    let result = [...savings];

    // Apply search filter
    if (searchTerm) {
      result = result.filter(saving =>
        saving.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        saving.notes.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      result = result.filter(saving => saving.goal_type === filterType);
    }

    setFilteredSavings(result);
  }, [savings, searchTerm, filterType, sortConfig]);

  const fetchSavings = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`${API_BASE_URL}/savings-goals/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.status === 401) {
        throw new Error('Session expired');
      }

      if (!response.ok) {
        throw new Error('Failed to fetch savings goals');
      }

      const data = await response.json();
      console.log(data);
      setSavings(data);
      setFilteredSavings(data);
    } catch (error) {
      console.error('Error fetching savings goals:', error);
      if (error.message === 'Session expired') {
        showSessionExpiredAlert();
      } else {
        setError('Failed to load savings goals. Please try again later.');
        showErrorAlert('Failed to load savings goals');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (savingId) => {
    const result = await Swal.fire({
      title: 'Delete Saving Goal',
      text: 'Are you sure you want to delete this saving goal? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/savings-goals/${savingId}/`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          setSavings(savings.filter(saving => saving.id !== savingId));
          showSuccessAlert('Saving goal deleted successfully');
        } else {
          throw new Error('Failed to delete saving goal');
        }
      } catch (error) {
        console.error('Error deleting saving goal:', error);
        showErrorAlert('Failed to delete saving goal');
      }
    }
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const showSuccessAlert = (message) => {
    Toast.fire({
      icon: 'success',
      title: message
    });
  };

  const showErrorAlert = (message) => {
    Toast.fire({
      icon: 'error',
      title: message
    });
  };

  const showSessionExpiredAlert = () => {
    Swal.fire({
      title: 'Session Expired',
      text: 'Your session has expired. Please log in again.',
      icon: 'warning',
      confirmButtonColor: '#1a237e',
      confirmButtonText: 'Go to Login',
      allowOutsideClick: false,
      allowEscapeKey: false
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/login');
      }
    });
  };

  const getGoalTypeLabel = (type) => {
    const types = {
      emergency: 'emergency',
      vacation: 'vacation',
      purchase: 'purchase',
      investment: 'investment',
      utilities: 'utilities',
      other: 'other',

    };
    return types[type] || type;
  };

  const getGoalTypeColor = (type) => {
    const colors = {
      vacation: '#4caf50',
      emergency: '#f44336',
      investment: '#2196f3',
      purchase: '#ff9800',
      utilities: '#0e4244ff',
      other: '#d3d036ff',


    };
    return colors[type] || '#757575';
  };

  const renderIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || FaWallet;
    return <IconComponent />;
  };

  if (loading) {
    return (
      <Layout>
        <div className="categories-loading">
          <FaSpinner className="spinner" />
          <p>Loading savings goals...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="categories-container">
        <div className="categories-header">
          <div className="header-content">
            <h1>Savings Goals</h1>
            <p>Manage your savings goals</p>
          </div>
          <button 
            className="create-category-btn"
            onClick={() => navigate('/create-savings-goal')}
          >
            <FaPlus /> Create Saving Goal
          </button>
        </div>

        <div className="categories-toolbar">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search transaction..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="toolbar-actions">
            <button 
              className="btn-secondary filter-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter /> Filter
            </button>
            <div className="sort-buttons">
              <button 
                className={`btn-secondary ${sortConfig.key === 'name' ? 'active' : ''}`}
                onClick={() => handleSort('name')}
              >
                <FaSort /> Name
                {sortConfig.key === 'name' && (
                  sortConfig.direction === 'asc' ? <FaArrowUp /> : <FaArrowDown />
                )}
              </button>
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="filter-panel">
            <div className="filter-group">
              <label>Category Type:</label>
              <select 
                value={filterType} 
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="emergency">Emergency Fund</option>
                <option value="vacation">Vacation</option>
                <option value="purchase">Major Purchase</option>
                <option value="investment">Investment</option>
                <option value="utilities">Utilities</option>
                <option value="other">Other</option>
                
              </select>
            </div>
          </div>
        )}

        {error && (
          <div className="error-banner">
            <FaExclamationTriangle />
            <span>{error}</span>
          </div>
        )}

        {filteredSavings.length === 0 ? (
          <div className="no-categories">
            <p>No savings goals found</p>
            {searchTerm || filterType !== 'all' ? (
              <button 
                className="btn-secondary"
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('all');
                }}
              >
                Clear Filters
              </button>
            ) : (
              <button 
                className="btn-primary"
                onClick={() => navigate('/create-category')}
              >
                Create Your First Saving Goal
              </button>
            )}
          </div>
        ) : (
          <div className="categories-grid">
            {filteredSavings.map(saving => (
              <div 
                key={saving.id} 
                className="category-card"
                style={{ borderColor: getGoalTypeColor(saving.goal_type) }}
              >
                <div 
                  className="category-icon"
                  style={{  }}
                >
                  {renderIcon()}
                </div>
                <div className="category-content">
                  <h3>{saving.name}</h3>
                  <p className="category-description">{saving.notes}</p>
                  <span 
                    className="category-type"
                    style={{ backgroundColor: getGoalTypeColor(saving.goal_type), padding: '10px', color: 'rgba(33, 30, 73, 1)', fontStyle: 'bold' }}
                  >
                    {getGoalTypeLabel(saving.goal_type) }

                  </span>
                  <span 
                    className="category-type"
                    style={{ backgroundColor: getGoalTypeColor(), padding: '10px', color: 'rgba(33, 30, 73, 1)', fontStyle: 'bold' }}
                  >
                    <span>Target</span>
                    &nbsp;&nbsp;${saving.target_amount}

                  </span>
                  <span 
                    className="category-type"
                    style={{ backgroundColor: getGoalTypeColor(), padding: '10px', color: 'rgba(33, 30, 73, 1)', fontStyle: 'bold' }}
                  >
                    <span>Current Balance</span>
                    &nbsp;&nbsp;${saving.current_amount}

                  </span>
                </div>
                <div className="category-actions">
                  <button 
                    className="btn-icon edit-btn"
                    onClick={() => navigate(`/categories/edit/${saving.id}`)}
                    title="Edit Category"
                  >
                    <FaEdit />
                  </button>
                  <button 
                    className="btn-icon delete-btn"
                    onClick={() => handleDelete(saving.id)}
                    title="Delete Category"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SavingsGoals;
